import { createSlice } from "@reduxjs/toolkit";
import { persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";
import { clearTokens, storeTokens } from "utils/auth.utils";
import { encryptTransform, decryptTransform } from "utils/encryption.utils";

/**
 * Initial state for the user slice
 */
const initialState = {
  // User profile information
  profile: {
    id: null,
    name: null,
    email: null,
    mobile: null,
    avatar: null,
    role: "user",
    customerRefId: null,
    isEmailVerified: false,
    isMobileVerified: false,
    isActive: false,
    kycStatus: null,
    raStatus: null,
  },

  // Auth tokens
  tokens: {
    accessToken: null,
    refreshToken: null,
    expiresIn: null,
  },

  // Authentication state
  isAuthenticated: false, // should default to false

  // User preferences
  preferences: {
    theme: "dark", // "light" | "dark"
    language: "en",
    notifications: true,
  },

  // UI and status states
  loading: false,
  error: null,
};

/**
 * User slice for authentication, profile, and preferences
 */
const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    /**
     * ✅ Handle login or signup success
     */
    loginSuccess: (state, action) => {
      const { accessToken, refreshToken, expiresIn, ...userData } = action.payload;
      
      storeTokens({ token: accessToken, refreshToken });
      
      state.isAuthenticated = true;
      state.profile = { ...state.profile, ...userData };
      state.tokens = {
        accessToken: accessToken || null,
        refreshToken: refreshToken || null,
        expiresIn: expiresIn || null,
      };
      state.error = null;
    },

    /**
     * ❌ Handle authentication failure
     */
    loginFailure: (state, action) => {
      state.isAuthenticated = false;
      state.error = action.payload || "Authentication failed";
      state.tokens = { accessToken: null, refreshToken: null, expiresIn: null };
    },

    /**
     * 🚪 Logout and clear session data
     */
    logout: (state) => {
      state.isAuthenticated = false;
      clearTokens();

      state.profile = {
        id: null,
        name: null,
        email: null,
        mobile: null,
        avatar: null,
        role: "user",
        customerRefId: null,
        isEmailVerified: false,
        isMobileVerified: false,
        isActive: false,
        kycStatus: null,
        raStatus: null,
      };

      state.tokens = {
        accessToken: null,
        refreshToken: null,
        expiresIn: null,
      };
    },

    /**
     * ✏️ Update profile data (partial update)
     */
    updateProfile: (state, action) => {
      state.profile = { ...state.profile, ...action.payload };
    },

    /**
     * 🔄 Update tokens (used for token refresh)
     */
    updateTokens: (state, action) => {
      const { accessToken, refreshToken, expiresIn } = action.payload;
      if (accessToken) state.tokens.accessToken = accessToken;
      if (refreshToken) state.tokens.refreshToken = refreshToken;
      if (expiresIn) state.tokens.expiresIn = expiresIn;
    },

    /**
     * 🎨 Theme preference actions
     */
    setTheme: (state, action) => {
      state.preferences.theme = action.payload;
    },

    toggleTheme: (state) => {
      state.preferences.theme =
        state.preferences.theme === "light" ? "dark" : "light";
    },

    /**
     * ⚙️ Update other user preferences
     */
    updatePreferences: (state, action) => {
      state.preferences = { ...state.preferences, ...action.payload };
    },
  },
});

// Export actions
export const {
  loginSuccess,
  loginFailure,
  logout,
  updateProfile,
  updateTokens,
  setTheme,
  toggleTheme,
  updatePreferences,
} = userSlice.actions;

// ✅ Selectors
export const selectUser = (state) => state.user;
export const selectIsAuthenticated = (state) => state.user.isAuthenticated;
export const selectUserProfile = (state) => state.user.profile;
export const selectUserTokens = (state) => state.user.tokens;
export const selectUserPreferences = (state) => state.user.preferences;
export const selectTheme = (state) => state.user.preferences.theme;

// ✅ Redux Persist Configuration
const userPersistConfig = {
  key: "user",
  storage,
  whitelist: ["profile", "preferences", "tokens", "isAuthenticated"], // persist auth
  transforms: [encryptTransform, decryptTransform],
};

// Export persisted reducer
export default persistReducer(userPersistConfig, userSlice.reducer);
